import React, { useState, useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Copyright from '../internals/components/Copyright';

import DispatchSummary from './DispatchSummary';
import HighlightedCard from './HighlightedCard';
import PageViewsBarChart from './PageViewsBarChart';
import SessionsChart from './SessionsChart';
import StatCard from './StatCard';
import AppCurrentSubject from './AppCurrentSubject';
import { useSelector, useDispatch } from 'react-redux';

import {
  selectSmartmeterData, selectSmartmeterStatus, fetchSmartmeterDataWithFilters
} from '../../features/smartmeter/smartmeterSlice';

import {
  selectSumchargeData, selectSumchargeStatus, fetchSumchargeDataWithFilters
} from '../../features/sumcharge/sumchargeSlice';

import {
  selectPriceData, selectPriceStatus, fetchPriceDataWithFilters
} from '../../features/price/priceSlice';

import {
  selectCurrentstatusData,
  selectCurrentstatusStatus,
  fetchCurrentstatusDataWithFilters
} from '../../features/currentstatus/currentstatusSlice';

import {
  selectVehicleData, selectVehicleStatus, fetchVehicleDataWithFilters
} from '../../features/vehicle/vehicleSlice';

import {
  selectOptimizationData,
  selectOptimizationStatus, fetchOptimizationDataWithFilters
} from '../../features/optimization/optimizationSlice';
import {
  selectPredictionData,
  selectPredictionStatus,
  fetchPredictionDataWithFilters
} from '../../features/prediction/predictionSlice';
import {
  selectBuildingforecastStatus,
  fetchBuildingforecastDataWithFilters,
  selectBuildingforecastData
} from '../../features/buildingforecast/buildingforecastSlice';
import {
  selectQueryDate
} from '../../features/uidata/uidataSlice';
import PagedCars from './PagedCars'
import {  
  getMinTime,
  getMaxTime
} from '../utils/datetimeUtils';
import CarCharging from './CarCharging';

import {
  selectVehicledetailsData, fetchVehicledetailsDataWithFilters
} from '../../features/vehicledetails/vehicledetailsSlice';

import CarStatus from './CarStatus';
import CarCharginBlue from './CarCharginBlue';
import CustomizedDataGrid from './CustomizedDataGrid';
function sumValuesByTime(data, interval = "hour") {
  return Object.values(
    data.reduce((acc, { date, value }) => {
      const d = new Date(date);
      let groupDate;

      if (interval === "date") {
        groupDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
      } else if (false && interval === "hour") {
        groupDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours());
      } else if (interval === "hour" || interval === "10min") {
        const minutes = Math.floor(d.getUTCMinutes() / 10) * 10;
        groupDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), minutes);
      } else {
        throw new Error("Invalid interval. Use 'date', 'hour', or '10min'.");
      }
      const key = groupDate.getTime(); // Using timestamp as key for uniqueness
      if (!acc[key]) {
        acc[key] = { date: groupDate, value: 0 };
      }
      acc[key].value += value;
      return acc;
    }, {})
  );
}
function smartmeterGroupByTime({ smartMeter, startDate, endDate }) {
  const data = [];
  for (let i = 0; i < smartMeter.length; i++) {
    const d = new Date((smartMeter[i].time_count) * 1000);
    if (smartMeter[i]['Converted delta Energy Sum Element']) {
      if (smartMeter[i]['Converted delta Energy Sum Element'] && d.getTime() >= startDate.getTime() && d.getTime() <= endDate.getTime()) {
        data.push({
          date: d,
          value: smartMeter[i]['Converted delta Energy Sum Element']
        });
      }
    }
  }
  const result = {
    smartmeter: {}
  };
  result.smartmeter.data = data; //sumValuesByTime(data, 'hour');
  result.smartmeter.ByDay = sumValuesByTime(data, 'date');
  return result.smartmeter;
}
function chargeGroupByTime({ sumCharge, startDate, endDate }) {
  const { time_range, sum_charge, sum_discharge } = sumCharge;
  if (!!time_range === false) {
    return { charges: [] }
  }
  const charges = [];
  const discharges = [];
  for (let i = 0; i < time_range.length; i++) {
    const date = new Date(time_range[i]);
    if (date.getTime() >= startDate.getTime() && date.getTime() <= endDate.getTime()) {
      charges.push({
        date,
        value: sum_charge[i]
      });
      discharges.push({
        date,
        value: sum_discharge[i]
      });
    }
  }
  const result = { charges: {}, discharges: {} };
  result.charges.data = charges; //sumValuesByTime(charges, 'hour');
  result.charges.ByDay = sumValuesByTime(charges, 'date');
  result.discharges.data = discharges; // sumValuesByTime(discharges, 'hour');
  result.discharges.ByDay = sumValuesByTime(discharges, 'date');
  return result;
}



function getDayDifference(date1, date2) {
  // Convert both dates to milliseconds
  const time1 = new Date(date1).getTime();
  const time2 = new Date(date2).getTime();
  // Calculate the difference in milliseconds
  const diffInMilliseconds = Math.abs(time1 - time2);
  // Convert milliseconds to days
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
  return Math.floor(diffInDays);
}
function sumArray(data) {
  return data.reduce((sum, item) => sum + item.value, 0);
}


function buildChargeGraphData(dataArr, startDate, endDate) {
  if (!!dataArr === false || Array.isArray(dataArr) === false || !dataArr.length) {
    return [];
  }

  const sumCharge = dataArr?.[0] || 0;
  const lastDays = getDayDifference(startDate, endDate);
  const chargeResult = chargeGroupByTime({ sumCharge, startDate, endDate });
  const dischargeTotal = sumArray(chargeResult.discharges.ByDay);
  const chargesTotal = sumArray(chargeResult.charges.ByDay);
  return {
    chargeResult,
    chargesTotal,
    dischargeTotal,
    stacks: [
      {
        title: 'Vehicles Charges',
        value: `${chargesTotal.toFixed(2)} kWh`,
        interval: `Last ${lastDays} days`,
        trend: 'up',
        data: chargeResult.charges.data,
      },
      {
        title: 'Vehicle DisCharges',
        value: `${dischargeTotal.toFixed(2)} kWh`,
        interval: `Last ${lastDays} days`,
        trend: 'down',
        data: chargeResult.discharges.data,
      },
      {
        title: 'Vehicle DisCharges',
        value: `${dischargeTotal.toFixed(2)} kWh`,
        interval: `Last ${lastDays} days`,
        trend: 'down',
        data: chargeResult.discharges.data,
      },
      // {
      //   title: 'Vehicle count',
      //   value: 0,
      //   interval: `Last ${lastDays} days`,
      //   trend: 'neutral',
      //   data: vehicleCount,
      // },
    ]
  }
}

function buildSmartmeterGraphData(dataArr, startDate) {
  if (!!dataArr === false) {
    return null;
  }
  const smartmeterUsage = smartmeterGroupByTime({
    smartMeter: dataArr,
    startDate,
    endDate: new Date()
  });
  return smartmeterUsage;

}
function generateMongoFilter(field, startDate, endDate) {
  return {
    [field]: {
      $gte: startDate,
      $lte: endDate
    }
  };
}

export default function MainGridv2() {

  const dispatch = useDispatch();
  // const chargeAPIData = useSelector(selectChargerData);
  // const chargeStatus = useSelector(selectChargerStatus);
  const smartmeterAPIData = useSelector(selectSmartmeterData);
  const buildingforecastData = useSelector(selectBuildingforecastData);
  const vehicleAPIData = useSelector(selectVehicleData);
  const predictionAPIData = useSelector(selectPredictionData);
  const optimizationAPIData = useSelector(selectOptimizationData);
  const currentstatusAPIData = useSelector(selectCurrentstatusData);
  const priceAPIData = useSelector(selectPriceData);
  const sumchargeAPIData = useSelector(selectSumchargeData);
  //
  const smartmeterStatus = useSelector(selectSmartmeterStatus);
  const vehicleStatus = useSelector(selectVehicleStatus);
  const predictionStatus = useSelector(selectPredictionStatus);
  const currentstatusStatus = useSelector(selectCurrentstatusStatus);
  const sumchargeStatus = useSelector(selectSumchargeStatus);
  const priceStatus = useSelector(selectPriceStatus);
  const buildingforecastStatus = useSelector(selectBuildingforecastStatus);
  const optimizationStatus = useSelector(selectOptimizationStatus);

  const vehicledetailsAPIData = useSelector(selectVehicledetailsData);

  //
  const { beginingDate: tempBeginingDate, endDate: tempEndDate } = useSelector(selectQueryDate);
  const beginingDate = new Date(tempBeginingDate);
  const endDate = new Date(tempEndDate);
  const lastDays = getDayDifference(beginingDate, endDate);
  const [vehicleState, setVehicleState] = useState(null);



  useEffect(() => {
    if (currentstatusStatus === 'succeeded' && currentstatusStatus === 'succeeded' &&
      predictionStatus === 'succeeded' && optimizationStatus === 'succeeded'
      && vehicleStatus === 'succeeded' && Array.isArray(vehicleAPIData?.results)) {
      const result = [];
      const set = new Set();
      const connectedVins = currentstatusAPIData?.results?.[0]?.parking || [];
      for (const connectedVin of connectedVins) {
        const vehicleInfo = vehicleAPIData.results.find(d => d.VIN === connectedVin) || {};
        const temp = { VIN: connectedVin, ...vehicleInfo };
        if (!set.has(temp.VIN)) {
          set.add(temp.VIN);
          dispatch(fetchVehicledetailsDataWithFilters({ filter: { vin: temp.VIN } }));
          temp.prediction = predictionAPIData.results.find(d => d.vin === temp.VIN) || [];
          temp.optimization = optimizationAPIData.results.find(d => d.vin === temp.VIN) || [];
          if (temp.prediction && temp.optimization) {
            result.push(temp);
          }
        }
      }
      setVehicleState(result);
    }
  }, [predictionStatus, optimizationStatus, vehicleStatus]);

  const apiAvailable = (status) => ['idle', 'succeeded'].includes(status)
  useEffect(() => {
    if (apiAvailable(currentstatusStatus)) {
      dispatch(fetchCurrentstatusDataWithFilters({}));
    }
    if (apiAvailable(priceStatus)) {
      dispatch(fetchPriceDataWithFilters({}));
    }
    if (apiAvailable(sumchargeStatus)) {
      // created_at
      dispatch(fetchSumchargeDataWithFilters({}));
    }
    if (apiAvailable(predictionStatus)) {
      const filter = generateMongoFilter('arrival_time', beginingDate, endDate);
      dispatch(fetchPredictionDataWithFilters({ filter }));
    }
    if (apiAvailable(buildingforecastStatus)) {
      const filter = {
        // start_time: {
        //   $lte: beginingDate,
        // },
        // end_time: {
        // $gte: endDate.getTime()
        // },
      };
      dispatch(fetchBuildingforecastDataWithFilters({ filter }));
    }
    if (apiAvailable(optimizationStatus)) {
      const filter = generateMongoFilter('arrival_time', beginingDate, endDate);
      dispatch(fetchOptimizationDataWithFilters({ filter }));
    }
    if (apiAvailable(vehicleStatus)) {
      const filterEndTime = getMinTime(endDate, new Date());
      const filter = generateMongoFilter('time', beginingDate, filterEndTime);
      dispatch(fetchVehicleDataWithFilters({ filter }));
    }
    // if (apiAvailable(chargeStatus)) {
    //   const filterEndTime = getMinTime(endDate, new Date());
    //   const filter = generateMongoFilter('timestamp', beginingDate, filterEndTime);
    //   dispatch(fetchChargeDataWithFilters({ filter }));
    // }
    if (apiAvailable(smartmeterStatus)) {
      const filterEndTime = getMinTime(endDate, new Date());
      const filter = {
        time_count: {
          $gte: parseInt(beginingDate.getTime() / 1000), //  - (8 * 60),
          $lte: parseInt(filterEndTime / 1000) //  - (8 * 60)
        }
      };
      dispatch(fetchSmartmeterDataWithFilters({ filter }));
    }
  }, [tempBeginingDate, tempEndDate]);


  const sumchargeData = useMemo(() => {
    if (sumchargeAPIData?.results) {
      const filterEndTime = getMaxTime(endDate);
      return buildChargeGraphData(sumchargeAPIData.results, beginingDate, filterEndTime);
    }
    return [];
  }, [sumchargeAPIData?.results?.length, tempBeginingDate, tempEndDate]);

  const smartmeterData = useMemo(() => {
    if (smartmeterAPIData?.results) {
      const filterEndTime = getMinTime(endDate);
      return buildSmartmeterGraphData(smartmeterAPIData?.results?.filter(d => !!d.time), beginingDate, filterEndTime);
    }
    return {}
  }, [smartmeterAPIData?.results?.length, tempBeginingDate, tempEndDate]);
  const charges = [] // chargeAPIData?.results?.map(d => ({ date: new Date(d.timestamp), value: d.charge_total }))
  const discharges = [] // chargeAPIData?.results?.map(d => ({ date: new Date(d.timestamp), value: d.discharge_total }))
  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      {/* cards */}
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Overview DR
      </Typography>
      <Grid
        container
        spacing={2}
        columns={12}
        sx={{ mb: (theme) => theme.spacing(2) }}
      >
        <Grid size={{ xs: 12, sm: 12, lg: 12 }}>
          <PagedCars carData={vehicleState} detailsdata = {vehicledetailsAPIData} />
        </Grid>
        <Grid size={{ xs: 12, lg: 12 }}>
          <CarStatus />
        </Grid>
        <Grid size={{ xs: 12, lg: 12 }}>
          <CustomizedDataGrid />

        </Grid>
        <Grid size={{ xs: 12, lg: 12 }}>

          <CarCharginBlue />
        </Grid>
        <Grid size={{ xs: 12, lg: 12 }}>
          <CarCharging
            beginingDate={beginingDate}
            endDate={endDate} />
        </Grid>
        <Grid size={{ xs: 12, lg: 12 }}>
          <DispatchSummary
            beginingDate={beginingDate}
            endDate={endDate}

            charges={charges}
            discharges={discharges}

            scheduledCharging={sumchargeData?.chargeResult?.charges?.data}
            scheduledDischarging={sumchargeData?.chargeResult?.discharges?.data}


            smartmeterData={smartmeterData?.data}
            buildingforecast={buildingforecastData?.results}

            priceSummer={priceAPIData?.results?.[0].summer_price_signal}
            priceWinter={priceAPIData?.results?.[0].summer_price_signal}
            priceTimeRange={priceAPIData?.results?.[0].time_series}
          />
        </Grid>
        {sumchargeData?.stacks?.map((card, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard {...card} />
          </Grid>
        ))}
        <Grid size={{ xs: 12, md: 4 }}>
          <SessionsChart charges={sumchargeData.totalChargeSessionCount}
            discharges={sumchargeData.totalDischargeSessionCount} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <AppCurrentSubject chartType="line" key="line"
            charges={sumchargeData?.chargeResult?.charges.ByDay}
            discharges={sumchargeData?.chargeResult?.discharges.ByDay}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <PageViewsBarChart
            charges={smartmeterData?.ByDay}
            discharges={sumchargeData?.chargeResult?.discharges.ByDay}
            title={`Discharges and from Grid for the last ${lastDays} days`}
          />
        </Grid>
        {/* <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <HighlightedCard />
        </Grid> */}
        {/* <Grid size={{ xs: 12, md: 4 }}>
          <AppCurrentSubject chartType="radar" key="radar" startTime={3} charges={chargeData.chargesByDay} discharges={chargeData.dischargesByDay} />
        </Grid> */}
        {/* <Grid size={{ xs: 12, md: 4 }}>
          <AppCurrentSubject chartType="heatmap" key="heatmap" startTime={4} charges={chargeData.chargesByDay} discharges={chargeData.dischargesByDay} />
        </Grid> */}
      </Grid>
      <Grid container spacing={2} columns={12}>
        {/* <Grid size={{ xs: 12, lg: 9 }}>
          <DispatchSummary />
        </Grid> */}
      </Grid>
      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}
